import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Dialog } from "@/components/ui/Dialog";
import { Icon } from "@/components/ui/Icon";
import { WorkspaceBadge } from "@/components/ui/WorkspaceBadge";
import { useRowShares } from "@/hooks/useRowShares";
import { useUserSearch } from "@/hooks/useUserSearch";
import { api } from "@/lib/api";
import { resolveWorkspaceColor } from "@/lib/colors";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import type { UUID, Workspace } from "@/lib/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rowId: UUID;
  /** The ticket's home workspace — never offered as a department share target. */
  homeWorkspaceId: UUID;
}

export function ShareRowDialog({ open, onOpenChange, rowId, homeWorkspaceId }: Props) {
  const { shares, addPeople, addDepartments, removePerson, removeDepartment } = useRowShares(
    rowId,
    open,
  );
  const wsQuery = useQuery({
    queryKey: ["workspaces", "all"],
    queryFn: api.listAllWorkspaces,
    enabled: open,
  });

  // Inline person search — NOT a nested popover. The dialog is a focus-trapping
  // Radix modal, so a portaled PersonPicker input never receives keystrokes;
  // the search lives directly in the dialog body instead.
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(q), 180);
    return () => window.clearTimeout(id);
  }, [q]);
  const search = useUserSearch(debounced, debounced.trim().length >= 1);

  const sharedUserIds = new Set(shares.users.map((u) => u.user_id));
  const sharedWsIds = new Set(shares.departments.map((d) => d.workspace_id));
  const deptCandidates: Workspace[] = (wsQuery.data ?? []).filter((w) => w.id !== homeWorkspaceId);
  const results = (search.data ?? []).filter((u) => !sharedUserIds.has(u.id));

  async function addPerson(userId: UUID) {
    try {
      await addPeople.mutateAsync([userId]);
      showToast(t.shareRowAdded, "success");
      setQ("");
      setDebounced("");
    } catch (err) {
      showToast(err instanceof Error ? err.message : t.unknownError, "error");
    }
  }

  async function toggleDept(ws: Workspace) {
    try {
      if (sharedWsIds.has(ws.id)) {
        await removeDepartment.mutateAsync(ws.id);
        showToast(t.shareRowRemoved, "success");
      } else {
        await addDepartments.mutateAsync([ws.id]);
        showToast(t.shareRowAdded, "success");
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : t.unknownError, "error");
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t.shareRowTitle}
      description={t.shareRowDescription}
      width={480}
    >
      {/* PERSONEN */}
      <div style={{ marginTop: 4 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-2)", marginBottom: 6 }}>
          {t.shareRowPeopleHd}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {shares.users.length === 0 ? (
            <span style={{ fontSize: 12.5, color: "var(--ink-3)" }}>{t.shareRowNoShares}</span>
          ) : (
            shares.users.map((u) => (
              <span
                key={u.user_id}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 12.5,
                  color: "var(--ink-1)",
                  background: "var(--surface-soft)",
                  border: "1px solid var(--hairline)",
                  borderRadius: 999,
                  padding: "3px 6px 3px 10px",
                }}
              >
                {u.display_name ?? u.username ?? u.email}
                <button
                  type="button"
                  onClick={() => void removePerson.mutateAsync(u.user_id)}
                  style={{
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    padding: 0,
                    display: "inline-flex",
                  }}
                  title={t.shareRowRemoved}
                >
                  <Icon name="x" size={13} color="var(--ink-3)" />
                </button>
              </span>
            ))
          )}
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t.assignSearchPlaceholder}
          style={{
            width: "100%",
            height: 34,
            padding: "0 11px",
            fontSize: 13,
            borderRadius: 8,
            border: "1px solid var(--hairline-strong)",
            background: "var(--surface)",
            color: "var(--ink-1)",
            outline: "none",
            fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
          }}
        />
        {debounced.trim().length >= 1 ? (
          <div
            style={{
              marginTop: 4,
              border: "1px solid var(--hairline-soft)",
              borderRadius: 8,
              maxHeight: 200,
              overflowY: "auto",
            }}
          >
            {results.length === 0 ? (
              <div style={{ padding: "12px 12px", fontSize: 12.5, color: "var(--ink-4)" }}>
                {search.isLoading ? "…" : t.assignNoResults}
              </div>
            ) : (
              results.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => void addPerson(u.id)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 12px",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    fontSize: 13,
                    color: "var(--ink-1)",
                    fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: 600 }}>{u.display_name ?? u.email}</span>
                    {u.username ? (
                      <span style={{ marginLeft: 6, color: "var(--ink-4)", fontSize: 11.5 }}>
                        ({u.username.toUpperCase()})
                      </span>
                    ) : null}
                    <div
                      style={{
                        fontSize: 11.5,
                        color: "var(--ink-3)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {u.email}
                    </div>
                  </div>
                  <Icon name="plus" size={14} color="var(--accent)" />
                </button>
              ))
            )}
          </div>
        ) : null}
      </div>

      {/* ABTEILUNGEN */}
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-2)", marginBottom: 6 }}>
          {t.shareRowDeptsHd}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {deptCandidates.map((ws) => {
            const isShared = sharedWsIds.has(ws.id);
            return (
              <button
                key={ws.id}
                type="button"
                onClick={() => void toggleDept(ws)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  padding: "8px 11px",
                  background: isShared ? "var(--surface-soft)" : "transparent",
                  border: `1px solid ${isShared ? "var(--hairline)" : "var(--hairline-soft)"}`,
                  borderRadius: 8,
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <WorkspaceBadge name={ws.name} icon={ws.icon} color={resolveWorkspaceColor(ws)} />
                <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: "var(--ink-1)" }}>
                  {ws.name}
                </span>
                {isShared ? (
                  <Icon name="check" size={14} color="var(--accent)" stroke={2.4} />
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      <div
        style={{
          marginTop: 12,
          paddingTop: 10,
          borderTop: "1px solid var(--hairline-soft)",
          fontSize: 11.5,
          color: "var(--ink-3)",
          lineHeight: 1.4,
        }}
      >
        {t.shareRowHint}
      </div>
    </Dialog>
  );
}
